import { useMemo, useState } from "react";
import TableComponent from "../../components/common/TableComponent";
import PaginationComponent from "../../components/PaginationComponent";
import Loading from "../../components/common/LoaderCommon";
import {
  useInfiniteGetInvoiceBalanceAmountReport,
  useDownloadBalanceAmountReport,
} from "../../service/reportsService";
import DownloadButton from "./DownloadFile";
import { ReusableCombobox } from "../../utils/components/ui/combobox";
import { useInfiniteGetAllCustomer } from "../../service/customerService";
import { useGetAllUsers } from "../../service/userService";
import { X } from "lucide-react";

type Props = {};

function BalanceAmountReport({}: Props) {
  const [currentPage, setCurrentPage] = useState(0);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerComboboxOpen, setCustomerComboboxOpen] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");
  const [selectedStaff, setSelectedStaff] = useState<number | "">("");
  const [staffComboboxOpen, setStaffComboboxOpen] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  const downloadMutation = useDownloadBalanceAmountReport();

  const { data, isLoading } = useInfiniteGetInvoiceBalanceAmountReport({
    pageSize: 10,
    pageNo: 0,
    startDate,
    endDate,
    clientId: selectedCustomer || undefined,
    staffId: selectedStaff || undefined,
  });

  const { data: customerData } = useInfiniteGetAllCustomer({
    pageSize: 10,
    pageNo: 0,
    customerName: customerSearch,
  });

  const { data: usersData } = useGetAllUsers({
    pageSize: 100,
    pageNo: 0,
    userName: staffSearch,
  });

  const totalPages = data?.pages[0]?.totalPages || 0;
  const balanceData = data?.pages[0]?.balanceAmountResponseList || [];

  const customerOptions =
    customerData?.pages
      ?.flatMap((page) => page.customerResponse || [])
      .map((customer: any) => ({
        value: customer.id,
        label: customer.username,
      })) || [];

  const staffOptions = useMemo(() => {
    const users = usersData?.userDetailsResponse;
    if (!users) return [];
    // If it's an array, map directly; if single object, wrap
    const list = Array.isArray(users) ? users : [users];
    return list.map((user: any) => ({
      value: String(user.id),
      label: user.username || user.email,
    }));
  }, [usersData]);

  const balanceColumns = useMemo(
    () => [
      { header: "Invoice Number", accessor: "invoiceNumber" },
      { header: "Invoice Date", accessor: "invoiceDate" },
      { header: "Total Purchased Amount", accessor: "totalPurchasedAmount" },
      { header: "Total Paid Amount", accessor: "totalPaidAmount" },
      { header: "Balance Amount", accessor: "balanceAmount" },
      { header: "Customer Name", accessor: "customerName" },
      { header: "Staff Name", accessor: "staffName" },
      { header: "Phone Number", accessor: "phoneNumber" },
      { header: "Email", accessor: "email" },
    ],
    [],
  );

  const isFilterActive = !!selectedCustomer || !!selectedStaff;

  const handleResetFilters = () => {
    setSelectedCustomer("");
    setCustomerSearch("");
    setSelectedStaff("");
    setStaffSearch("");
    setCurrentPage(0);
  };

  return (
    <div className="space-y-4">
      {/* Filter card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
        <div className="flex justify-between items-end flex-wrap gap-4">
          <div className="flex gap-4 flex-wrap items-end">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-[#F5F8FD] border-[#E5ECF8] rounded-[12px] border font-normal h-11 px-3 min-w-[160px] text-sm text-[#21314A] outline-none focus:border-[#1A5EF9]"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-[#F5F8FD] border-[#E5ECF8] rounded-[12px] border font-normal h-11 px-3 min-w-[160px] text-sm text-[#21314A] outline-none focus:border-[#1A5EF9]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                Customer
              </label>
              <div className="min-w-[240px]">
                <ReusableCombobox
                  options={customerOptions}
                  value={selectedCustomer}
                  onValueChange={(value) => {
                    setSelectedCustomer(value as string);
                  }}
                  open={customerComboboxOpen}
                  setOpen={setCustomerComboboxOpen}
                  placeholder="Select Customer"
                  searchPlaceholder="Search customer..."
                  emptyText="No customers found."
                  className="bg-[#F5F8FD] border-[#E5ECF8] rounded-[12px] border font-normal h-11"
                  onInputChange={(val) => setCustomerSearch(val)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                Staff
              </label>
              <div className="min-w-[240px]">
                <ReusableCombobox
                  options={staffOptions}
                  value={selectedStaff ? String(selectedStaff) : ""}
                  onValueChange={(value) => {
                    setSelectedStaff(value ? Number(value) : "");
                  }}
                  open={staffComboboxOpen}
                  setOpen={setStaffComboboxOpen}
                  placeholder="Select Staff"
                  searchPlaceholder="Search staff..."
                  emptyText="No staff found."
                  className="bg-[#F5F8FD] border-[#E5ECF8] rounded-[12px] border font-normal h-11"
                  onInputChange={(val) => setStaffSearch(val)}
                />
              </div>
            </div>

            {isFilterActive && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="h-11 rounded-[12px] border border-[#E5ECF8] px-5 text-[#52627C] font-medium text-sm flex items-center gap-2 hover:bg-[#F5F8FD] transition-colors self-end"
              >
                <X className="w-4 h-4" />
                Reset
              </button>
            )}
          </div>
          <DownloadButton
            mutation={downloadMutation}
            customerId={selectedCustomer}
            startDate={startDate}
            endDate={endDate}
          />
        </div>
      </div>

      {/* Table card */}
      {isLoading ? (
        <Loading />
      ) : (
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
          <div className="flex justify-between items-center border-b border-[#E8EEF9] py-3 px-6">
            <div className="flex items-center gap-6">
              <p className="text-sm text-[#52627C]">
                <span className="font-semibold text-[#21314A]">
                  {data?.pages[0]?.totalElements || 0}
                </span>{" "}
                Balance amount entries found
              </p>
              {data?.pages[0]?.grandTotalBalanceAmount !== undefined && (
                <p className="text-sm text-[#52627C] border-l border-[#E8EEF9] pl-6">
                  Grand Total Balance:{" "}
                  <span className="font-semibold text-[#1A5EF9]">
                    {Number(data.pages[0].grandTotalBalanceAmount).toFixed(3)}
                  </span>
                </p>
              )}
            </div>
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
          <TableComponent
            caption="A list of balance amount report entries."
            columns={balanceColumns}
            data={balanceData || []}
          />
        </div>
      )}
    </div>
  );
}

export default BalanceAmountReport;
